import { css } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { ReactElement, ReactNode, useCallback, useEffect, useRef } from 'react';
import { ChatDialogEvent, ChatState } from 'sdk';

import { useSDK, useUIState } from '../../../../app/App';
import { styled } from '../../../../styled';
import type { Theme } from '../../../../theme/interface/detailed';
import { ReplyIcon } from '../../../icons/replyIcon';

type Interface = {
	className: string;
	message: ChatDialogEvent;
	children: ReactNode;
	isInvolve?: boolean;
};

const Component = ({ className, message, children, isInvolve }: Interface): ReactElement => {
	const ref = useRef<HTMLDivElement>(null);
	const { id } = message;
	const { eventsStore, uiStateStore, replyMessageStore } = useSDK();
	const { scrollStore } = useUIState();
	const onClickHandler = useCallback(() => {
		replyMessageStore.setRepliedMessage(message);
	}, [message, replyMessageStore]);

	useEffect(() => {
		const lastEvent = eventsStore.getLastEvent(undefined, true);
		if (lastEvent?.id === id && ref.current) {
			scrollStore.setLastMessageRef(ref.current);
		}
	}, [id, ref, eventsStore, scrollStore]);

	return (
		<div ref={ref} className={className}>
			<div>{children}</div>
			{uiStateStore.chatState === ChatState.chat && !uiStateStore.showRestartButton && !isInvolve && (
				<ReplyIcon onClick={onClickHandler} />
			)}
		</div>
	);
};

const Message = styled(observer(Component))(
	({
		theme: {
			chat: {
				messages: {
					message: {
						agentBackgroundColor,
						clientBackgroundColor,
						clientColor,
						agentColor,
						borderRadius,
						linkColor,
						...others
					},
				},
			},
		},
		fromClient = false,
	}: {
		theme: Theme;
		fromClient?: boolean;
	}) => ({
		backgroundColor: fromClient ? clientBackgroundColor : agentBackgroundColor,
		color: fromClient ? clientColor : agentColor,
		borderRadius: fromClient
			? `${borderRadius} ${borderRadius} 0 ${borderRadius}`
			: `${borderRadius} ${borderRadius} ${borderRadius} 0`,

		'& a': {
			color: linkColor,
		},
		'& i': {
			left: fromClient ? '-20px' : 'auto',
			right: fromClient ? 'auto' : '-20px',
			justifyContent: fromClient ? 'flex-start' : 'flex-end',
		},
		...others,
	}),
	css`
		display: inline-flex;
		overflow-wrap: normal;
		word-break: break-word;
		position: relative;
	`
);

export { Message };
